import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslations } from '../translations';
import { normalizeUnitType } from '../utils/unitType';
import './ThemedDropdown.css';
import './UnitTypeSelect.css';

interface UnitTypeSelectProps {
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  ariaDescribedBy?: string;
  className?: string;
  disabled?: boolean;
  invalid?: boolean;
}

const UnitTypeSelect: React.FC<UnitTypeSelectProps> = ({
  value,
  onChange,
  ariaLabel,
  ariaDescribedBy,
  className = '',
  disabled = false,
  invalid = false,
}) => {
  const t = useTranslations();
  const listboxId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const options = useMemo(
    () => Array.from(new Set(Object.values(t.createProduct.priceUnits).map(normalizeUnitType))),
    [t.createProduct.priceUnits],
  );
  const normalizedValue = normalizeUnitType(value);
  const selectedValue = options.find(
    (option) => option.localeCompare(normalizedValue, 'ro', { sensitivity: 'base' }) === 0,
  ) || normalizedValue;
  const filteredOptions = options.filter((option) => (
    option.toLocaleLowerCase('ro').includes(query.trim().toLocaleLowerCase('ro'))
  ));

  const close = () => {
    setOpen(false);
    setQuery('');
    setActiveIndex(0);
  };

  const select = (option: string) => {
    onChange(option);
    close();
  };

  useEffect(() => {
    const handleOutside = (event: MouseEvent | TouchEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) close();
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, []);

  return (
    <div
      className={`unit-type-select ${className}`.trim()}
      ref={rootRef}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) close();
      }}
    >
      <input
        type="text"
        className={invalid ? 'unit-type-select-invalid' : undefined}
        role="combobox"
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-invalid={invalid}
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-activedescendant={open && filteredOptions[activeIndex]
          ? `${listboxId}-option-${activeIndex}`
          : undefined}
        value={open ? query : selectedValue}
        disabled={disabled}
        placeholder={t.createProduct.priceUnitSearchPlaceholder}
        onFocus={() => {
          setQuery('');
          setActiveIndex(0);
          setOpen(true);
        }}
        onClick={() => {
          if (!open) {
            setQuery('');
            setActiveIndex(0);
            setOpen(true);
          }
        }}
        onChange={(event) => {
          setQuery(event.target.value);
          setActiveIndex(0);
          setOpen(true);
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setOpen(true);
            setActiveIndex((current) => Math.min(current + 1, filteredOptions.length - 1));
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveIndex((current) => Math.max(current - 1, 0));
          } else if (event.key === 'Enter' && open && filteredOptions[activeIndex]) {
            event.preventDefault();
            select(filteredOptions[activeIndex]);
          } else if (event.key === 'Escape') {
            event.preventDefault();
            close();
          }
        }}
      />
      <ChevronDown className={open ? 'open' : ''} size={18} aria-hidden />
      {open && (
        <div id={listboxId} className="unit-type-select-options themed-dropdown-menu" role="listbox">
          {filteredOptions.length ? filteredOptions.map((option, index) => (
            <button
              id={`${listboxId}-option-${index}`}
              type="button"
              role="option"
              aria-selected={option === selectedValue}
              className={`unit-type-select-option themed-dropdown-option ${option === selectedValue ? 'themed-dropdown-option-active' : ''}`}
              key={option}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => select(option)}
            >
              {option}
            </button>
          )) : (
            <div className="unit-type-select-empty">{t.createProduct.priceUnitNoResults}</div>
          )}
        </div>
      )}
    </div>
  );
};

export default UnitTypeSelect;
